import React, { useCallback, useEffect, useMemo } from 'react'

import {
  Calendar,
  CalendarGrid,
  CalendarCell,
  Button,
  Heading
} from 'react-aria-components'
import {
  CalendarDate,
  parseDate,
  getLocalTimeZone
} from '@internationalized/date'

import {
  FulfillmentType,
  ShippingBlackoutConfig
} from 'src/apollo/onlineOrdering'
import { t } from 'src/translations'

import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { useTimeBasedRules } from 'public/components/online_ordering/TimeBasedRuleContext'

import {
  calculateArrivalDate,
  calculateMinArrivalDate,
  calculateShipDate,
  isDateBlackedOut
} from './shippingDateUtils'

interface ShippingDateCalendarProps {
  fulfillmentDate: string | null | undefined
  setFulfillmentDate: (date: string | undefined) => void
  draftFulfillmentType: FulfillmentType
  estimatedDays?: number
  blackoutConfig?: ShippingBlackoutConfig | null
}

const ShippingDateCalendar = ({
  fulfillmentDate,
  setFulfillmentDate,
  draftFulfillmentType,
  estimatedDays,
  blackoutConfig
}: ShippingDateCalendarProps) => {
  const {
    selectedDiningOptionBehaviorSchedules,
    selectedDiningOptionBehavior
  } = useFulfillment()
  const {
    getFilteredFutureSchedules,
    itemInCartWithMaxLeadTime,
    getEarliestFulfillmentTime
  } = useTimeBasedRules()

  const timezone = getLocalTimeZone()

  // Calculate the minimum selectable arrival date based on shipping rate's estimated days
  const minDate = useMemo(
    () => calculateMinArrivalDate(estimatedDays, timezone),
    [estimatedDays, timezone]
  )

  const futureSchedules = useMemo(
    () =>
      getFilteredFutureSchedules(
        getEarliestFulfillmentTime(
          itemInCartWithMaxLeadTime?.itemGuid,
          selectedDiningOptionBehavior
        ),
        selectedDiningOptionBehaviorSchedules?.futureScheduleDates ?? [],
        selectedDiningOptionBehavior
      ),
    [
      selectedDiningOptionBehaviorSchedules,
      selectedDiningOptionBehavior,
      getFilteredFutureSchedules,
      getEarliestFulfillmentTime,
      itemInCartWithMaxLeadTime?.itemGuid
    ]
  )

  // Create a set of available dates for quick lookup
  const availableDates = useMemo(() => {
    const dates = new Set<string>()
    futureSchedules?.forEach((schedule) => {
      if (schedule.times.length > 0) {
        // Extract just the date part (YYYY-MM-DD) from the schedule date
        const datePart = schedule.date.split('T')[0]
        if (datePart) {
          dates.add(datePart)
        }
      }
    })
    return dates
  }, [futureSchedules])

  // Convert fulfillment date (ship date) to arrival date for calendar display
  const selectedCalendarDate = useMemo(() => {
    if (!fulfillmentDate) return null
    try {
      const datePart = fulfillmentDate.split('T')[0]
      if (!datePart) return null
      const shipDate = parseDate(datePart)
      // Convert ship date to arrival date for display
      return calculateArrivalDate(shipDate, estimatedDays, timezone)
    } catch {
      return null
    }
  }, [fulfillmentDate, estimatedDays, timezone])

  // Check if a date is unavailable
  // The calendar shows arrival dates, so we need to calculate the ship date
  // and check if there's a valid schedule for it
  const isDateUnavailable = useCallback(
    (date: CalendarDate) => {
      // Date is unavailable if it's before minimum arrival date
      if (date.compare(minDate) < 0) {
        return true
      }

      // Calculate the ship date for this arrival date
      const shipDate = calculateShipDate(date, estimatedDays, timezone)
      const shipDateStr = shipDate.toString()

      // Check if there's a schedule for the ship date
      if (!availableDates.has(shipDateStr)) {
        return true
      }

      // Check if the ship date is blacked out
      if (
        isDateBlackedOut(
          calculateArrivalDate(date, estimatedDays, timezone),
          blackoutConfig,
          timezone
        )
      ) {
        return true
      }

      return false
    },
    [availableDates, minDate, estimatedDays, timezone, blackoutConfig]
  )

  // Handle date selection
  // The user selects an arrival date, but we need to send the ship date to the server
  const handleDateChange = useCallback(
    (date: CalendarDate) => {
      // Calculate the ship date from the selected arrival date
      const shipDate = calculateShipDate(date, estimatedDays, timezone)
      const shipDateStr = shipDate.toString()

      // Find the matching schedule for the ship date and get the first time slot
      const schedule = futureSchedules?.find((s) =>
        s.date.startsWith(shipDateStr)
      )
      const firstTime = schedule?.times[0]?.time
      if (firstTime) {
        setFulfillmentDate(firstTime)
      }
    },
    [futureSchedules, setFulfillmentDate, estimatedDays, timezone]
  )

  // Auto-select first available date if none selected (respecting minDate and blackout dates)
  useEffect(() => {
    if (
      !fulfillmentDate &&
      draftFulfillmentType === FulfillmentType.Future &&
      futureSchedules?.length > 0
    ) {
      // Find first available arrival date that's on or after minDate
      // Then calculate the corresponding ship date
      const firstAvailableArrivalDate = (() => {
        // Start from minDate and find the first date that has a valid ship date schedule
        let checkDate = minDate
        const maxDaysToCheck = 60 // Safety limit

        for (let i = 0; i < maxDaysToCheck; i++) {
          const shipDate = calculateShipDate(checkDate, estimatedDays, timezone)
          const shipDateStr = shipDate.toString()

          // Check if there's a schedule for this ship date and it's not blacked out
          const schedule = futureSchedules.find(
            (s) => s.times.length > 0 && s.date.startsWith(shipDateStr)
          )

          if (
            schedule &&
            !isDateBlackedOut(shipDate, blackoutConfig, timezone)
          ) {
            return { arrivalDate: checkDate, schedule }
          }

          // Move to next day, skipping weekends for arrival dates
          checkDate = checkDate.add({ days: 1 })
          const dayOfWeek = checkDate.toDate(timezone).getDay()
          if (dayOfWeek === 0) checkDate = checkDate.add({ days: 1 }) // Skip Sunday
          if (dayOfWeek === 6) checkDate = checkDate.add({ days: 2 }) // Skip Saturday
        }

        return null
      })()

      if (firstAvailableArrivalDate) {
        const firstTime = firstAvailableArrivalDate.schedule.times[0]?.time
        if (firstTime) {
          setFulfillmentDate(firstTime)
        }
      }
    }
  }, [
    fulfillmentDate,
    draftFulfillmentType,
    futureSchedules,
    setFulfillmentDate,
    minDate,
    estimatedDays,
    timezone,
    blackoutConfig
  ])

  return (
    <div className='shippingDatePicker'>
      <div className='datePickerLabel'>{t('shipping-section.ship-date')}</div>
      <Calendar
        aria-label={t('shipping-section.select-shipping-date')}
        value={selectedCalendarDate}
        onChange={handleDateChange}
        isDateUnavailable={isDateUnavailable}
        minValue={minDate}
      >
        <header className='calendarHeader'>
          <Button slot='previous' className='calendarNavButton'>
            ‹
          </Button>
          <Heading className='calendarHeading' />
          <Button slot='next' className='calendarNavButton'>
            ›
          </Button>
        </header>
        <CalendarGrid className='calendarGrid'>
          {(date) => <CalendarCell date={date} className='calendarCell' />}
        </CalendarGrid>
      </Calendar>
    </div>
  )
}

export default ShippingDateCalendar
